import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import Swal from 'sweetalert2';

const MailTemplates = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    isDefault: false,
    isActive: true
  });

  // Fetch mail templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['mail-templates'],
    queryFn: async () => {
      const response = await api.get('/mail-templates');
      return response.data;
    }
  });

  // Fetch available variables
  const { data: variables = [] } = useQuery({
    queryKey: ['template-variables'],
    queryFn: async () => {
      const response = await api.get('/mail-templates/variables');
      return response.data;
    }
  });

  // Create/Update template mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editingTemplate) {
        const response = await api.put(`/mail-templates/${editingTemplate._id}`, data);
        return response.data;
      } else {
        const response = await api.post('/mail-templates', data);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mail-templates']);
      setIsModalOpen(false);
      setEditingTemplate(null);
      resetForm();
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: editingTemplate ? t('mailTemplate.templateUpdatedSuccess') : t('mailTemplate.templateCreatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('mailTemplate.templateSaveFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/mail-templates/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mail-templates']);
      Swal.fire({
        icon: 'success',
        title: t('mailTemplate.deleted'),
        text: t('mailTemplate.templateDeletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('mailTemplate.templateDeleteFailed'),
        confirmButtonText: t('common.ok')
      });
    }
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/mail-templates/${id}/set-default`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mail-templates']);
      Swal.fire({
        icon: 'success',
        title: t('mailTemplate.defaultSet'),
        text: t('mailTemplate.templateSetAsDefaultSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      body: '',
      isDefault: false,
      isActive: true
    });
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        isDefault: template.isDefault,
        isActive: template.isActive
      });
    } else {
      setEditingTemplate(null);
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('mailTemplate.confirmDelete'),
      text: t('mailTemplate.cannotRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('mailTemplate.yesDelete')
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(id);
      }
    });
  };

  const handleSetDefault = (id) => {
    setDefaultMutation.mutate(id);
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    const newText = before + variable + after;
    
    setFormData(prev => ({
      ...prev,
      body: newText
    }));
    
    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const setPresetTemplate = (templateType) => {
    const presetTemplates = {
      german: {
        name: 'German Invoice Email',
        subject: 'Rechnung Nr. {{RECHNUNGSNUMMER}} - Ihr Rechnung vom {{DATUM}}',
        body: `Sehr geehrte/r {{NAME}},

anbei erhalten Sie die Rechnung Nr. {{RECHNUNGSNUMMER}} vom {{DATUM}} 
für die erbrachten Leistungen.

Bitte überweisen Sie den Rechnungsbetrag in Höhe von {{BETRAG}} EUR 
bis spätestens zum {{FAELLIGKEITSDATUM}} auf folgendes Konto:

Kontoinhaber: {{FIRMENNAME}}
IBAN: {{IBAN}}
BIC: {{BIC}}
Bank: {{BANKNAME}}

Für Rückfragen stehen wir Ihnen jederzeit gerne zur Verfügung.

Mit freundlichen Grüßen  
{{FIRMENNAME}}`
      },
      english: {
        name: 'English Invoice Email', 
        subject: 'Invoice {{RECHNUNGSNUMMER}} - Your invoice dated {{DATUM}}',
        body: `Dear {{NAME}},

Please find attached your invoice {{RECHNUNGSNUMMER}} dated {{DATUM}} 
for services provided.

Please transfer the invoice amount of {{BETRAG}} EUR 
by no later than {{FAELLIGKEITSDATUM}} to the following account:

Account holder: {{FIRMENNAME}}
IBAN: {{IBAN}}
BIC: {{BIC}}
Bank: {{BANKNAME}}

We are available for any questions you may have.

Yours sincerely
{{FIRMENNAME}}`
      },
      turkish: {
        name: 'Turkish Invoice Email',
        subject: 'Fatura {{RECHNUNGSNUMMER}} - {{DATUM}} tarihli faturanız',
        body: `Sayın {{NAME}},

Ekte {{DATUM}} tarihli {{RECHNUNGSNUMMER}} numaralı faturanızı bulabilirsiniz.

Lütfen {{BETRAG}} EUR fatura tutarını {{FAELLIGKEITSDATUM}} tarihi öncesi 
aşağıdaki hesaba transfer ediniz:

Hesap sahibi: {{FIRMENNAME}}
IBAN: {{IBAN}}
BIC: {{BIC}}
Banka: {{BANKNAME}}

Sorularınız için her zaman ulaşabileceğiniz bizdripsiz.

Saygılarımızla
{{FIRMENNAME}}`
      },
      simple: {
        name: 'Simple Invoice Reminder',
        subject: 'Invoice {{RECHNUNGSNUMMER}} - {{BETRAG}} EUR',
        body: `Hello {{NAME}},

Your invoice {{RECHNUNGSNUMMER}} is ready.

Amount: {{BETRAG}} EUR
Due date: {{FAELLIGKEITSDATUM}}

Payment details:
Transfer to {{FIRMENNAME}} 
Account: {{IBAN}}

Thank you!
{{FIRMENNAME}}`
      }
    };

    if (presetTemplates[templateType]) {
      const template = presetTemplates[templateType];
      setFormData(prev => ({
        ...prev,
        name: template.name,
        subject: template.subject,
        body: template.body
      }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('mailTemplate.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('mailTemplate.subtitle')}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
        >
          {t('mailTemplate.addTemplate')}
        </button>
      </div>

      {/* Templates List */}
      <div className="card">
        {templates.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('mailTemplate.noTemplates')}</h3>
            <p className="text-gray-500 mb-4">{t('mailTemplate.createFirstTemplate')}</p>
            <button
              onClick={() => handleOpenModal()}
              className="btn btn-primary"
            >
              {t('mailTemplate.addTemplate')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('mailTemplate.name')}</th>
                  <th>{t('mailTemplate.subject')}</th>
                  <th>{t('mailTemplate.status')}</th>
                  <th>{t('mailTemplate.default')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template._id}>
                    <td className="font-medium">{template.name}</td>
                    <td className="max-w-xs truncate">{template.subject}</td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        template.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {template.isActive ? t('mailTemplate.active') : t('mailTemplate.inactive')}
                      </span>
                    </td>
                    <td>
                      {template.isDefault ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {t('mailTemplate.default')}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetDefault(template._id)}
                          className="text-blue-600 hover:text-blue-500 text-sm"
                        >
                          {t('mailTemplate.setDefault')}
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleOpenModal(template)}
                          className="text-blue-600 hover:text-blue-500 text-sm"
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(template._id)}
                          className="text-red-600 hover:text-red-500 text-sm"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingTemplate ? t('mailTemplate.editTemplate') : t('mailTemplate.addTemplate')}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('mailTemplate.templateName')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('mailTemplate.subject')} *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('mailTemplate.messageBody')} *
                  </label>
                  
                  {/* Preset Templates */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">{t('mailTemplate.quickTemplates')}</p>
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        type="button"
                        onClick={() => setPresetTemplate('german')}
                        className="text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm border"
                      >
                        {t('mailTemplate.germanTemplate')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPresetTemplate('english')}
                        className="text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm border"
                      >
                        {t('mailTemplate.englishTemplate')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPresetTemplate('turkish')}
                        className="text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm border"
                      >
                        {t('mailTemplate.turkishTemplate')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPresetTemplate('simple')}
                        className="text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm border"
                      >
                        {t('mailTemplate.simpleTemplate')}
                      </button>
                    </div>
                  </div>
                  
                  {/* Available Variables */}
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 mb-2">{t('mailTemplate.availableVariables')}</p>
                    <div className="flex flex-wrap gap-2">
                      {variables.map((variable) => (
                        <button
                          key={variable.key}
                          type="button"
                          onClick={() => insertVariable(variable.key)}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                          title={variable.description}
                        >
                          {variable.key}
                        </button>
                      ))}
                    </div>
                  </div>

                  <textarea
                    id="body"
                    name="body"
                    value={formData.body}
                    onChange={handleChange}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isDefault"
                      checked={formData.isDefault}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{t('mailTemplate.setAsDefault')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{t('mailTemplate.active')}</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={saveMutation.isPending}
                    className="btn btn-primary"
                  >
                    {saveMutation.isPending ? t('common.saving') : (editingTemplate ? t('mailTemplate.update') : t('mailTemplate.create'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailTemplates;
